import { useState } from "react";
import { contactMatchesFollowUp } from "../model/followUpRules";
import { emptyStyle, errorStyle } from "../styles/materielStyles";
import { AddFollowUpPatientModal } from "./AddFollowUpPatientModal";
import { FollowUpMergeConflictModal, MedicalEquipmentImportModal } from "./FollowUpDialogs";
import { FollowUpGrid } from "./FollowUpGrid";
import { FollowUpPatientModal } from "./FollowUpPatientModal";
import { ColumnColorBar, FollowUpFilters, FollowUpHeader } from "./FollowUpToolbar";
import { FollowUpVerticalView } from "./FollowUpVerticalView";
import { useFollowUpData, useFollowUpSaving } from "./useFollowUpRows";
import { useColumnColors, useFollowUpFilters, useFollowUpImport, useFollowUpPatientActions } from "./useFollowUpTools";

function FollowUpContent({ data, filters, viewMode, colors, onEditPatient, onPatch }) {
    const { loading, rows } = data;
    const { filteredRows, selectedRow, setSelectedId } = filters;
    if (loading) return <div style={emptyStyle}>Chargement...</div>;
    if (rows.length === 0) return <div style={emptyStyle}>Aucune ligne de suivi.</div>;
    if (filteredRows.length === 0) return <div style={emptyStyle}>Aucun résultat pour ces filtres.</div>;
    if (viewMode === "vertical") {
        return (
            <FollowUpVerticalView
                rows={filteredRows}
                selectedRow={selectedRow}
                onSelect={row => setSelectedId(row.id)}
                onEditPatient={onEditPatient}
                onPatch={(row, patch) => onPatch(row.id, patch)}
            />
        );
    }
    return (
        <FollowUpGrid
            rows={filteredRows}
            colors={colors}
            onEditPatient={onEditPatient}
            onPatch={(id, value) => onPatch(id, value)}
        />
    );
}

function FollowUpModals({ data, saving, patients, importer }) {
    const { editedPatient, setEditedPatient, addFormOpen, setAddFormOpen, addRow, savePatient, deletePatient } = patients;
    const { savingId, mergeConflict, chooseMerge, cancelMerge, confirmMerge } = saving;
    return (
        <>
            {editedPatient && (
                <FollowUpPatientModal
                    row={editedPatient}
                    contacts={data.contacts.filter(contact => contactMatchesFollowUp(contact, editedPatient))}
                    saving={savingId === editedPatient.id}
                    onClose={() => setEditedPatient(null)}
                    onSave={savePatient}
                    onDelete={deletePatient}
                />
            )}
            {addFormOpen && (
                <AddFollowUpPatientModal saving={savingId === "new"} onClose={() => setAddFormOpen(false)} onSave={addRow} />
            )}
            {importer.importOpen && (
                <MedicalEquipmentImportModal
                    file={importer.importFile}
                    report={importer.importReport}
                    importing={importer.importing}
                    onFileChange={importer.changeFile}
                    onCheck={() => importer.importWorkbook(true)}
                    onImport={() => importer.importWorkbook(false)}
                    onClose={() => !importer.importing && importer.setImportOpen(false)}
                />
            )}
            {mergeConflict && (
                <FollowUpMergeConflictModal
                    conflict={mergeConflict}
                    saving={savingId === mergeConflict.id}
                    onChoice={chooseMerge}
                    onCancel={cancelMerge}
                    onConfirm={confirmMerge}
                />
            )}
        </>
    );
}

// Tableau de suivi du matériel loué : une ligne par patient et matériel, enregistrée automatiquement.
export function FollowUpTable({ serviceKey, isSuperAdmin }) {
    const data = useFollowUpData(serviceKey);
    const saving = useFollowUpSaving(data);
    const filters = useFollowUpFilters(data.rows);
    const colors = useColumnColors(data);
    const importer = useFollowUpImport(data);
    const patients = useFollowUpPatientActions({ data, saving, setSelectedId: filters.setSelectedId });
    const [viewMode, setViewMode] = useState("vertical");

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <FollowUpHeader
                rows={data.rows}
                filters={filters}
                savingId={saving.savingId}
                viewMode={viewMode}
                setViewMode={setViewMode}
                isSuperAdmin={isSuperAdmin}
                onImport={() => importer.setImportOpen(true)}
                refresh={data.refresh}
                onAdd={() => patients.setAddFormOpen(true)}
            />
            <FollowUpFilters filters={filters} />
            {viewMode === "table" && colors.selectedColumn && <ColumnColorBar colors={colors} />}
            {data.error && <div role="alert" style={errorStyle}>{data.error}</div>}
            <div style={{ flex: 1, overflow: "auto" }}>
                <FollowUpContent
                    data={data}
                    filters={filters}
                    viewMode={viewMode}
                    colors={colors}
                    onEditPatient={patients.setEditedPatient}
                    onPatch={saving.patchRow}
                />
            </div>
            <FollowUpModals data={data} saving={saving} patients={patients} importer={importer} />
        </div>
    );
}
